import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: "top" | "bottom";
  maxWidth?: number;
  delay?: number;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = "top",
  maxWidth = 280,
  delay = 300,
}) => {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipHeight = 120; // approximate max height

    if (position === "top") {
      // If not enough room above, flip to bottom
      const useBottom = rect.top < tooltipHeight + 8;
      setCoords({
        top: useBottom ? rect.bottom + 8 : rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    } else {
      setCoords({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, [position]);

  const handleEnter = () => {
    timeoutRef.current = setTimeout(() => {
      updatePosition();
      setShow(true);
    }, delay);
  };

  const handleLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShow(false);
  };

  // Recalculate position if window scrolls while tooltip is showing
  useEffect(() => {
    if (!show) return;
    const handleScroll = () => updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [show, updatePosition]);

  const tooltipStyle: React.CSSProperties = {
    position: "fixed",
    top: position === "top" ? undefined : coords.top,
    bottom: position === "top" ? `calc(100vh - ${coords.top}px)` : undefined,
    left: coords.left,
    transform: "translateX(-50%)",
    maxWidth,
    zIndex: 9999,
    pointerEvents: "none",
  };

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
      {createPortal(
        <AnimatePresence>
          {show && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              style={tooltipStyle}
            >
              <div className="bg-slate-800 text-white text-xs rounded-xl px-3 py-2.5 shadow-lg leading-relaxed">
                {content}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;
